import { type FC, memo, useCallback, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useSetAtom } from "jotai";
import type { EventType } from "src/http/types";
import { useAppContext } from "src/shared/context/AppContext";
import type { HomeAwayOptions, PriceBuckets } from "src/shared/enums/enums";
import { type Dates, DateType } from "src/types/shared/cookies";
import { HorizontalCalendar } from "src/website-apps/responsive/stubhub-performer/components/calendar/mobile/HorizontalCalendar";
import {
  dateControl,
  eventTypeControl,
  filterIconControl,
  filterRowRecipe,
  rightControls,
  stickyContainer,
} from "src/website-apps/responsive/stubhub-performer/components/calendar/mobile/mobile-calendar-filter-row/styles";
import { useScrollSync } from "src/website-apps/responsive/stubhub-performer/components/calendar/mobile/useScrollSync";
import { useStickyCalendarEventListener } from "src/website-apps/responsive/stubhub-performer/components/calendar/mobile/useStickyCalendarEventListener";
import { useDateCookieOptions, withSetDatesCookie } from "src/website-apps/shared/context/date-context";
import { useEventsGridContext } from "src/website-apps/shared/context/events-grid-context";
import { useLocaleDayjs } from "src/website-apps/shared/hooks/useLocaleDayjs";
import { calendarFilterDrawerOpenAtom } from "src/website-apps/shared/utils/filterDrawerOpenAtom";
import { MobileCalendarDateSelector } from "./MobileCalendarDateSelector";
import { MobileCalendarEventTypeSelector } from "./MobileCalendarEventTypeSelector";
import { MobileCalendarFilterIcon } from "./MobileCalendarFilterIcon";

const EMPTY_SCROLL_SYNC_OPTIONS = {};

export const MobileCalendarFilterRow: FC = memo(() => {
  const appContext = useAppContext();
  const { env } = appContext;
  const [state, dispatch, { setGridFilterType, setHomeAwayFilterType, setMedianPriceBucketFilter, setDates }] =
    useEventsGridContext();
  const { filters, calendarMonthsWithEventsAsDates } = state;
  const { gridFilterType, homeAwayFilterType, dates, medianPriceBucketFilter } = filters;

  const onFilterTypeChange = useCallback(
    (val: EventType, isVenueIdReset = false, isNonEventGridTab = false, venueIdResetOverride = null) => {
      setGridFilterType(dispatch, state, val, isVenueIdReset, isNonEventGridTab, venueIdResetOverride);
    },
    [state, setGridFilterType, dispatch],
  );

  const onHomeAwayFilterTypeChange = useCallback(
    (val: (typeof HomeAwayOptions)[keyof typeof HomeAwayOptions]) => {
      setHomeAwayFilterType(dispatch, state, val);
    },
    [dispatch, state, setHomeAwayFilterType],
  );

  const onPriceChange = useCallback(
    (val: PriceBuckets[]) => {
      setMedianPriceBucketFilter(dispatch, state, val);
    },
    [dispatch, state, setMedianPriceBucketFilter],
  );

  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const setCalendarFilterDrawerOpen = useSetAtom(calendarFilterDrawerOpenAtom);

  const firstDateWithEvents = useMemo(() => {
    const events = state.calendarEvents?.pagedEvents?.items ?? [];
    if (events.length > 0) {
      const firstEvent = events[0];
      if (firstEvent) {
        // Use epoch millis + venue UTC offset so the scroll target matches the
        // day the event falls on at the venue, regardless of the viewer's timezone.
        const eventStartMs = firstEvent.eventMetadata?.common?.eventStartDateTime;
        const venueOffsetMinutes = (firstEvent.eventMetadata?.common?.venueTimeZoneOffset ?? 0) / (60 * 1000);
        const eventDayjs = eventStartMs
          ? dayjs(eventStartMs).utcOffset(venueOffsetMinutes)
          : dayjs(firstEvent.dateTime ?? "");
        return eventDayjs.format("YYYY-MM-DD");
      }
    }
    return undefined;
  }, [state.calendarEvents?.pagedEvents?.items]);

  const { activeDateString, scrollToDate, setActiveDateString } = useScrollSync(
    EMPTY_SCROLL_SYNC_OPTIONS,
    firstDateWithEvents,
  );
  const { stickyContainerRef, isContainerStuck } = useStickyCalendarEventListener();

  const handleToggleFiltersDrawer = useCallback(() => {
    const nextOpen = !filtersDrawerOpen;
    setFiltersDrawerOpen(nextOpen);
    setCalendarFilterDrawerOpen(nextOpen);
  }, [filtersDrawerOpen, setCalendarFilterDrawerOpen]);

  const handleDateClick = useCallback(
    (_dayOfMonth: number, dateString: string) => {
      scrollToDate(dateString);
      setActiveDateString(dateString);
    },
    [scrollToDate, setActiveDateString],
  );

  const dateCookieOptions = useDateCookieOptions();
  const execWithSetDatesCookie = useMemo(
    () => ({
      withSetDatesCookie,
      processGetDates: () => dates,
      dateCookieOptions,
    }),
    [dates, dateCookieOptions],
  );
  const currentDates = useMemo(() => execWithSetDatesCookie.processGetDates(), [execWithSetDatesCookie]);
  const localeDayjs = useLocaleDayjs();

  const visibleMonthsLabel = useMemo(() => {
    // @ts-ignore -- dates.type does evaluate to ALL_DATES despite ESLint claiming otherwise
    const isDefaultAllDates = !dates?.type || dates.type === DateType.ALL_DATES;
    if (!isDefaultAllDates) return undefined;
    if (calendarMonthsWithEventsAsDates.length === 0) return undefined;
    const first = calendarMonthsWithEventsAsDates[0];
    const last = calendarMonthsWithEventsAsDates.at(-1);
    const firstMonth = localeDayjs(first!).format("MMMM");
    const lastMonth = localeDayjs(last!).format("MMMM");
    return firstMonth === lastMonth ? firstMonth : `${firstMonth} - ${lastMonth}`;
  }, [calendarMonthsWithEventsAsDates, dates, localeDayjs]);

  const onDatesChange = (val: Dates) => {
    const setWithCookie = execWithSetDatesCookie.withSetDatesCookie(
      setDates,
      env,
      execWithSetDatesCookie.dateCookieOptions,
    );
    setWithCookie(dispatch, state, val);
  };

  return (
    <div ref={stickyContainerRef} className={stickyContainer}>
      <div className={filterRowRecipe({ position: isContainerStuck ? "stuck" : "static" })}>
        <div className={dateControl}>
          <MobileCalendarDateSelector
            dates={currentDates ?? undefined}
            onDatesChange={onDatesChange}
            labelOverride={visibleMonthsLabel}
          />
        </div>
        <div className={rightControls}>
          <div className={eventTypeControl}>
            <MobileCalendarEventTypeSelector
              selectedEventType={gridFilterType}
              onEventTypeChange={onFilterTypeChange}
              otherDrawerOpen={filtersDrawerOpen}
            />
          </div>
          <div className={filterIconControl}>
            <MobileCalendarFilterIcon
              isDrawerOpen={filtersDrawerOpen}
              onToggleDrawer={handleToggleFiltersDrawer}
              homeAwayValue={homeAwayFilterType}
              onHomeAwayChange={onHomeAwayFilterTypeChange}
              priceValue={medianPriceBucketFilter ?? []}
              onPriceChange={onPriceChange}
            />
          </div>
        </div>
      </div>

      <HorizontalCalendar activeDateString={activeDateString} onDateClick={handleDateClick} />
    </div>
  );
});
MobileCalendarFilterRow.displayName = "MobileCalendarFilterRow";
